import { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

import { useSetVoiceNoteFieldDefaultValue } from '@/react-query/videa-notes/useSetVoiceNoteFieldDefaultValue';
import { useSetVoiceNoteFieldRequired } from '@/react-query/videa-notes/useSetVoiceNoteFieldRequired';
import { useVoiceNoteFields } from '@/react-query/videa-notes/useVoiceNoteFields';

import { ambientViewTypeAtom } from '../../../patientList/atoms';

import { AddFieldValueModal } from './AddFieldValueModal';
import { FieldDetailPanel } from './FieldDetailPanel';
import {
  formatFieldKeyForDisplay,
  getDefaultValueDisplay,
  hasDefaultValue,
} from './fieldDisplayHelpers';
import type { FieldListItem } from './FieldList';
import { FieldListPanel } from './FieldListPanel';
import { PageHeader } from './PageHeader';

export const TemplateFieldSettingsPage = () => {
  // hooks
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useVoiceNoteFields();
  const setDefaultValue = useSetVoiceNoteFieldDefaultValue();
  const setRequired = useSetVoiceNoteFieldRequired();

  // atoms
  const setAmbientViewType = useSetAtom(ambientViewTypeAtom);

  // state
  const [fieldSearch, setFieldSearch] = useState('');
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [isAddValueModalOpen, setIsAddValueModalOpen] = useState(false);
  const [changesSavedAt, setChangesSavedAt] = useState<number | null>(null);

  const fields = useMemo(() => {
    const list = data?.fields ?? [];
    return [...list].sort((a, b) => a.key.localeCompare(b.key));
  }, [data?.fields]);

  const filteredFields = useMemo(() => {
    if (!fieldSearch.trim()) return fields;
    const q = fieldSearch.toLowerCase().trim();
    return fields.filter(
      (f) =>
        f.key.toLowerCase().includes(q) ||
        f.label.toLowerCase().includes(q) ||
        getDefaultValueDisplay(f).toLowerCase().includes(q),
    );
  }, [fields, fieldSearch]);

  const fieldListItems: FieldListItem[] = useMemo(
    () =>
      filteredFields.map((f) => ({
        key: f.key,
        displayLabel: f.label || formatFieldKeyForDisplay(f.key),
        defaultLabel: hasDefaultValue(f)
          ? getDefaultValueDisplay(f)
          : undefined,
      })),
    [filteredFields],
  );

  const selectedField = useMemo(
    () => fields.find((f) => f.key === selectedFieldKey) ?? null,
    [fields, selectedFieldKey],
  );

  const suggestedValues = useMemo(
    () => selectedField?.constraints?.suggestedValues ?? [],
    [selectedField],
  );
  const defaultDisplayValue = useMemo(
    () =>
      selectedField?.defaultValue != null
        ? String(selectedField.defaultValue)
        : null,
    [selectedField],
  );

  const handleAddValueSuccess = () => {
    setIsAddValueModalOpen(false);
    setChangesSavedAt(Date.now());
    void queryClient.invalidateQueries({ queryKey: ['voice-note-fields'] });
  };

  const handleSetDefault = (value: string) => {
    if (!selectedField) return;
    setDefaultValue.mutate(
      { fieldKey: selectedField.key, defaultValue: value },
      { onSuccess: () => setChangesSavedAt(Date.now()) },
    );
  };

  const handleSetRequired = (required: boolean) => {
    if (!selectedField) return;
    setRequired.mutate(
      { fieldKey: selectedField.key, required },
      { onSuccess: () => setChangesSavedAt(Date.now()) },
    );
  };

  useEffect(() => {
    setAmbientViewType('templates');
  }, [setAmbientViewType]);

  useEffect(() => {
    if (changesSavedAt == null) return;
    const timer = setTimeout(() => setChangesSavedAt(null), 3000);
    return () => clearTimeout(timer);
  }, [changesSavedAt]);

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-screen vai-overflow-hidden vai-bg-white">
      <PageHeader onBackClick={() => setAmbientViewType('templates')} />
      <div className="vai-flex-1 vai-flex vai-min-h-0 vai-m-6 vai-gap-6">
        <FieldListPanel
          fieldSearch={fieldSearch}
          fieldListItems={fieldListItems}
          selectedFieldKey={selectedFieldKey}
          isLoading={isLoading}
          isError={isError}
          onSelectField={setSelectedFieldKey}
          onFieldSearchChange={setFieldSearch}
        />
        <FieldDetailPanel
          selectedField={selectedField}
          changesSavedAt={changesSavedAt}
          suggestedValues={suggestedValues}
          defaultDisplayValue={defaultDisplayValue}
          isSetDefaultPending={setDefaultValue.isLoading}
          isSetRequiredPending={setRequired.isLoading}
          onAddValue={() => setIsAddValueModalOpen(true)}
          onSetDefault={handleSetDefault}
          onSetRequired={handleSetRequired}
        />
      </div>
      {selectedField && (
        <AddFieldValueModal
          fieldKey={selectedField.key}
          fieldLabel={selectedField.label || undefined}
          isOpen={isAddValueModalOpen}
          onOpenChange={setIsAddValueModalOpen}
          onSuccess={handleAddValueSuccess}
        />
      )}
    </div>
  );
};
